import { getRuntimeApiBaseUrl } from 'config/runtimeConfig';
import { ApiError, AuthError, NetworkError } from 'lib/errors';
import { getAuthToken } from './authClient';

interface RequestOptions extends RequestInit {
  skipAuth?: boolean;
}

const buildHeaders = async (
  skipAuth: boolean,
  extra?: HeadersInit,
): Promise<Record<string, string>> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (!skipAuth) {
    const token = await getAuthToken();
    headers.Authorization = `Bearer ${token}`;
  }

  if (extra) {
    Object.entries(extra as Record<string, string>).forEach(([k, v]) => {
      headers[k] = v;
    });
  }

  return headers;
};

const errorFromResponse = async (response: Response): Promise<ApiError> => {
  const body = await response.text().catch(() => '');
  // Preserve the legacy message format `Request failed with <status>: <body>`
  // so existing callers and tests keep matching. Callers that need typed
  // branching read `.status` / `.code` instead of regex-matching the text.
  const message = `Request failed with ${response.status}: ${body}`;
  if (response.status === 401 || response.status === 403) {
    return new AuthError({ status: response.status, message, body });
  }

  return new ApiError({
    status: response.status,
    code: `HTTP_${response.status}`,
    message,
    body,
  });
};

export const apiRequest = async <TResponse>(
  path: string,
  options: RequestOptions = {},
): Promise<TResponse> => {
  const { skipAuth = false, headers, ...rest } = options;
  const apiBaseUrl = getRuntimeApiBaseUrl();

  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl}${path}`, {
      ...rest,
      headers: await buildHeaders(skipAuth, headers),
    });
  } catch (err) {
    throw new NetworkError({
      message: err instanceof Error ? err.message : 'Network request failed',
    });
  }

  if (!response.ok) {
    throw await errorFromResponse(response);
  }

  if (response.status === 204) {
    return undefined as TResponse;
  }

  return (await response.json()) as TResponse;
};

/**
 * Variant for callers that need to send a non-JSON body (FormData, blob,
 * raw bytes). Same auth header injection and error normalisation as
 * `apiRequest`, but the caller owns Content-Type and request body.
 */
export const apiRequestRaw = async <TResponse>(
  path: string,
  init: Omit<RequestOptions, 'body'> & { body: BodyInit },
): Promise<TResponse> => {
  const { skipAuth = false, headers, ...rest } = init;
  const apiBaseUrl = getRuntimeApiBaseUrl();

  const finalHeaders: Record<string, string> = {};
  if (!skipAuth) {
    const token = await getAuthToken();
    finalHeaders.Authorization = `Bearer ${token}`;
  }

  if (headers) {
    Object.entries(headers as Record<string, string>).forEach(([k, v]) => {
      finalHeaders[k] = v;
    });
  }

  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl}${path}`, { ...rest, headers: finalHeaders });
  } catch (err) {
    throw new NetworkError({
      message: err instanceof Error ? err.message : 'Network request failed',
    });
  }

  if (!response.ok) throw await errorFromResponse(response);
  if (response.status === 204) return undefined as TResponse;
  return (await response.json()) as TResponse;
};
